import { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, FileUp, Sparkles, BookOpenCheck, FileDown } from "lucide-react";

/**
 * Self-contained animated walkthrough that plays the 4-step Financials Ready flow.
 * No external video — pure CSS/JS animation on a styled "screen".
 *
 * Each step shows a styled mock UI rendered in the screen frame, with a
 * progress bar and chapter chips along the bottom. Auto-advances and loops.
 */
const STEPS = [
  {
    key: "upload",
    icon: FileUp,
    label: "Upload",
    title: "Drop in a statement",
    sub: "PDF, CSV, or Excel from any bank",
    durationMs: 4200,
  },
  {
    key: "parse",
    icon: Sparkles,
    label: "Parse & categorize",
    title: "Claude Sonnet 4.5 categorizes every line",
    sub: "Auto-mapped to a clean P&L taxonomy",
    durationMs: 4500,
  },
  {
    key: "reconcile",
    icon: BookOpenCheck,
    label: "Reconcile",
    title: "Match every cent against the bank",
    sub: "Side-by-side compare with mismatch highlights",
    durationMs: 4500,
  },
  {
    key: "export",
    icon: FileDown,
    label: "Export",
    title: "Boardroom-ready PDFs & Excel",
    sub: "P&L · Balance Sheet · Cash Flow · Expenses",
    durationMs: 4500,
  },
];

export default function WorkflowVideo() {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!playing) return;
    const ms = STEPS[idx].durationMs;
    timerRef.current = setTimeout(() => setIdx((i) => (i + 1) % STEPS.length), ms);
    return () => clearTimeout(timerRef.current);
  }, [idx, playing]);

  const step = STEPS[idx];

  return (
    <section id="how" className="border-b relative overflow-hidden" style={{ borderColor: "var(--ll-border)" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.2em] text-black mb-3">A simple, deliberate workflow</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] mb-6">
            Four steps. Then you&apos;re closing the books.
          </h2>
          <p className="text-base text-[var(--ll-ink-soft)] leading-relaxed max-w-md mb-7">
            Watch a complete close cycle in under twenty seconds. Each step on the right is exactly what happens inside Financials Ready.
          </p>
          <ol className="space-y-3">
            {STEPS.map((s, i) => (
              <li
                key={s.key}
                className={`grid grid-cols-[auto,1fr] gap-4 items-start cursor-pointer transition-opacity ${i === idx ? "opacity-100" : "opacity-50 hover:opacity-80"}`}
                onClick={() => { setIdx(i); setPlaying(false); }}
                data-testid={`workflow-step-${s.key}`}
              >
                <span
                  className={`w-7 h-7 grid place-items-center font-mono-fin text-xs border ${i === idx ? "bg-[var(--ll-ink)] text-white border-[var(--ll-ink)]" : "border-[var(--ll-border)]"}`}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <div className="font-display font-bold text-base leading-tight">{s.label}</div>
                  <div className="text-xs text-[var(--ll-ink-soft)] leading-relaxed">{s.sub}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Screen */}
        <div className="lg:col-span-7">
          <div
            className="relative border bg-[var(--ll-bg-card)] shadow-[10px_10px_0_0_var(--ll-ink)]"
            style={{ borderColor: "var(--ll-ink)" }}
            data-testid="workflow-video"
          >
            {/* Window chrome */}
            <div className="flex items-center gap-1.5 px-3 py-2 border-b" style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg-sidebar)" }}>
              <span className="w-2.5 h-2.5 rounded-full bg-[#FCA5A5]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#FCD34D]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#86EFAC]" />
              <span className="ml-3 text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)] font-mono-fin">financialsready / live demo</span>
              <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] font-mono-fin">{String(idx + 1).padStart(2, "0")} · {step.label}</span>
            </div>

            <div className="relative h-[420px] overflow-hidden">
              {STEPS.map((s, i) => (
                <div
                  key={s.key}
                  className={`absolute inset-0 transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                  aria-hidden={i !== idx}
                >
                  {i === 0 && <UploadScene active={i === idx} />}
                  {i === 1 && <ParseScene active={i === idx} />}
                  {i === 2 && <ReconcileScene active={i === idx} />}
                  {i === 3 && <ExportScene active={i === idx} />}
                </div>
              ))}

              {/* Caption */}
              <div
                key={`cap-${idx}`}
                className="absolute left-0 right-0 bottom-0 px-6 py-4 border-t bg-[var(--ll-bg-card)]/95 backdrop-blur"
                style={{ borderColor: "var(--ll-border)", animation: "ll-fade-up 0.5s ease both" }}
              >
                <div className="flex items-center gap-3">
                  <step.icon size={16} />
                  <div className="font-display font-bold text-base">{step.title}</div>
                </div>
              </div>
            </div>

            {/* Progress + controls */}
            <div className="px-3 py-2 border-t flex items-center gap-3" style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg-sidebar)" }}>
              <button
                onClick={() => setPlaying((p) => !p)}
                data-testid="workflow-play-toggle"
                aria-label={playing ? "Pause walkthrough" : "Play walkthrough"}
                className="w-7 h-7 grid place-items-center bg-[var(--ll-ink)] text-white hover:bg-[#2d6a4f]"
              >
                {playing ? <Pause size={12} /> : <Play size={12} />}
              </button>
              <button
                onClick={() => { setIdx(0); setPlaying(true); }}
                data-testid="workflow-restart"
                aria-label="Restart walkthrough"
                className="w-7 h-7 grid place-items-center border hover:bg-white"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <RotateCcw size={12} />
              </button>
              <div className="flex-1 flex items-center gap-1">
                {STEPS.map((s, i) => (
                  <div key={s.key} className="flex-1 h-1 bg-[var(--ll-border)] relative overflow-hidden">
                    <div
                      key={`${i}-${idx}-${playing}`}
                      className="absolute inset-y-0 left-0 bg-[var(--ll-ink)]"
                      style={{
                        width: i < idx ? "100%" : i > idx ? "0%" : "0%",
                        animation: i === idx && playing ? `ll-fill ${s.durationMs}ms linear forwards` : "none",
                      }}
                    />
                  </div>
                ))}
              </div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] font-mono-fin">
                {idx + 1} / {STEPS.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ll-fill { from { width: 0%; } to { width: 100%; } }
        @keyframes ll-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ll-row-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ll-tag-pop { 0% { opacity: 0; transform: scale(0.85); } 70% { transform: scale(1.04); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes ll-bar-grow { from { transform: scaleY(0); } to { transform: scaleY(1); } }
        @keyframes ll-cursor { 0% { transform: translate(8%, 80%); opacity: 0; } 15% { opacity: 1; } 90% { transform: translate(60%, 40%); opacity: 1; } 100% { transform: translate(60%, 40%); opacity: 0; } }
      `}</style>
    </section>
  );
}

/* ---------- Scenes ---------- */
function UploadScene({ active }) {
  return (
    <div className="absolute inset-0 grid place-items-center bg-[var(--ll-bg-sidebar)]">
      <div className="w-[78%] max-w-md border-2 border-dashed text-center py-12 px-6 bg-white"
        style={{ borderColor: active ? "var(--ll-ink)" : "var(--ll-border)" }}>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)] mb-3">Upload statement</div>
        <div className="font-display font-bold text-xl mb-2">Chase_Operating_2026-01.pdf</div>
        <div className="text-xs text-[var(--ll-ink-soft)] mb-5">12 pages · 247 transactions</div>
        <div className="relative h-1.5 bg-[var(--ll-border)] overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-[var(--ll-ink)]"
            style={{ width: "0%", animation: active ? "ll-fill 3.5s ease-out forwards" : "none" }} />
        </div>
        <div className="mt-4 text-[10px] font-mono-fin uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">parsing · ocr · dedup</div>
      </div>
    </div>
  );
}

function ParseScene({ active }) {
  const rows = [
    { date: "Jan 03", desc: "STRIPE PAYOUT", amount: "+$8,420.00", cat: "Sales Revenue", color: "#ECFDF5", text: "#065F46" },
    { date: "Jan 04", desc: "WeWork Membership", amount: "-$1,250.00", cat: "Rent & Lease", color: "#FEFCE8", text: "#854D0E" },
    { date: "Jan 07", desc: "Google Workspace", amount: "-$72.00", cat: "Software & SaaS", color: "#EFF6FF", text: "#1E40AF" },
    { date: "Jan 09", desc: "Vendor — ACME LLC", amount: "-$2,100.00", cat: "Professional Services", color: "#FFF7ED", text: "#9A3412" },
    { date: "Jan 11", desc: "Square Receivable", amount: "+$3,140.55", cat: "Sales Revenue", color: "#ECFDF5", text: "#065F46" },
    { date: "Jan 12", desc: "Delta Airlines", amount: "-$845.20", cat: "Travel", color: "#FEF2F2", text: "#991B1B" },
  ];
  return (
    <div className="absolute inset-0 p-5 bg-white overflow-hidden">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)] mb-2">AI categorization · live</div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
            <th className="text-left py-1.5 pr-2">Date</th>
            <th className="text-left py-1.5 pr-2">Description</th>
            <th className="text-right py-1.5 pr-2">Amount</th>
            <th className="text-left py-1.5">Category</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              className="border-t"
              style={{
                borderColor: "var(--ll-border)",
                animation: active ? `ll-row-in 0.4s ease ${0.2 + i * 0.35}s both` : "none",
              }}
            >
              <td className="py-2 pr-2 font-mono-fin">{r.date}</td>
              <td className="py-2 pr-2">{r.desc}</td>
              <td className="py-2 pr-2 text-right font-mono-fin">{r.amount}</td>
              <td className="py-2">
                <span
                  className="inline-block text-[10px] uppercase tracking-widest px-2 py-0.5"
                  style={{
                    background: r.color,
                    color: r.text,
                    animation: active ? `ll-tag-pop 0.45s ease ${0.5 + i * 0.35}s both` : "none",
                  }}
                >
                  {r.cat}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReconcileScene({ active }) {
  const rows = [
    { date: "Jan 03", desc: "STRIPE PAYOUT", stmt: "$20,920.00", comp: "$20,920.00", match: true },
    { date: "Jan 04", desc: "WeWork Membership", stmt: "$19,670.00", comp: "$19,670.00", match: true },
    { date: "Jan 07", desc: "Google Workspace", stmt: "$19,598.00", comp: "$19,598.00", match: true },
    { date: "Jan 09", desc: "Vendor — ACME LLC", stmt: "$17,498.00", comp: "$17,498.00", match: true },
    { date: "Jan 11", desc: "Square Receivable", stmt: "$20,638.55", comp: "$20,638.55", match: true },
    { date: "Jan 12", desc: "Delta Airlines", stmt: "$19,793.35", comp: "$19,793.35", match: true },
  ];
  return (
    <div className="absolute inset-0 p-5 bg-white overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)]">Reconciliation · Operating Checking</div>
        <div className="text-[10px] font-mono-fin px-2 py-0.5 bg-[#ECFDF5] text-[#065F46] uppercase tracking-widest">All matched</div>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
            <th className="text-left py-1.5 pr-2">Date</th>
            <th className="text-left py-1.5 pr-2">Description</th>
            <th className="text-right py-1.5 pr-2">Statement</th>
            <th className="text-right py-1.5 pr-2">Computed</th>
            <th className="text-right py-1.5">✓</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t" style={{
              borderColor: "var(--ll-border)",
              animation: active ? `ll-row-in 0.4s ease ${0.15 + i * 0.3}s both` : "none",
            }}>
              <td className="py-2 pr-2 font-mono-fin">{r.date}</td>
              <td className="py-2 pr-2">{r.desc}</td>
              <td className="py-2 pr-2 text-right font-mono-fin">{r.stmt}</td>
              <td className="py-2 pr-2 text-right font-mono-fin">{r.comp}</td>
              <td className="py-2 text-right">
                <span className="inline-block w-4 h-4 grid place-items-center bg-[#10B981] text-white text-[10px]"
                  style={{ animation: active ? `ll-tag-pop 0.4s ease ${0.5 + i * 0.3}s both` : "none" }}>✓</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExportScene({ active }) {
  const bars = [70, 45, 90, 60, 35, 55, 80, 50];
  return (
    <div className="absolute inset-0 p-5 bg-white overflow-hidden grid grid-cols-2 gap-4">
      {/* Mock cover */}
      <div className="border p-4 flex flex-col bg-[var(--ll-bg-sidebar)]" style={{ borderColor: "var(--ll-ink)" }}>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)] mb-2">Period close package</div>
        <div className="font-display font-bold text-xl leading-tight">Test LLC</div>
        <div className="text-xs text-[var(--ll-ink-soft)] mb-4">Jan 1 – Jan 31, 2026</div>
        <ul className="text-xs space-y-1.5">
          <li className="flex items-center justify-between"><span>Total revenue</span><span className="font-mono-fin">$28,640.55</span></li>
          <li className="flex items-center justify-between"><span>Total expenses</span><span className="font-mono-fin">$11,212.40</span></li>
          <li className="flex items-center justify-between border-t pt-1.5 mt-1.5" style={{ borderColor: "var(--ll-border)" }}>
            <span className="font-medium">Net income</span><span className="font-mono-fin font-medium">$17,428.15</span>
          </li>
        </ul>
        <div className="mt-auto pt-3 text-[10px] font-mono-fin uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">P&amp;L · BS · CF · Expenses</div>
      </div>

      {/* Mock chart */}
      <div className="border p-4 flex flex-col" style={{ borderColor: "var(--ll-border)" }}>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)] mb-2">Cash position · YTD</div>
        <div className="text-2xl font-mono-fin font-medium">$847,231.40</div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ochre)] mb-3">+8.4% MoM</div>
        <div className="flex-1 flex items-end gap-2">
          {bars.map((h, i) => (
            <div
              key={i}
              className="flex-1 bg-[var(--ll-ink)]"
              style={{
                height: `${h}%`,
                transformOrigin: "bottom",
                animation: active ? `ll-bar-grow 0.5s ease ${0.2 + i * 0.12}s both` : "none",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
